import type { MatchCard, MatchesResponse, MatchView } from '@ballpeek/api/client'
import type { MatchesSearch, MatchesSearchInput } from './search-schema'

import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { footballCoreHref } from '~/lib/navigation/football-core'
import { useCurrentRelativeLocation } from '~/lib/navigation/use-current-relative-location'
import { PublicFootballRequestError } from '~/lib/public-football-read'
import { matchesQueryOptions } from './query-options'
import { resolveMatchesSearch } from './search-schema'

interface MatchesViewProps {
  data: MatchesResponse | undefined
  search: MatchesSearch
  origin: string
  isPending: boolean
  isFetching: boolean
  error: Error | null
  onRetry: () => void
}

const MATCH_VIEWS: MatchView[] = ['live', 'upcoming', 'finished', 'changes']

function matchesHref(
  locale: string,
  search: MatchesSearch,
  updates: Partial<MatchesSearch>,
): string {
  const next = { ...search, ...updates }
  const query = new URLSearchParams()
  query.set('view', next.view)
  if (next.competition !== undefined)
    query.set('competition', next.competition)
  if (next.season !== undefined)
    query.set('season', String(next.season))
  if (next.team !== undefined)
    query.set('team', next.team)
  if (next.date !== undefined)
    query.set('date', next.date)
  if (next.timeZone !== 'UTC')
    query.set('timeZone', next.timeZone)
  if (next.page > 1)
    query.set('page', String(next.page))
  return footballCoreHref(locale, '/matches', { query })
}

function formatFixtureTime(value: string | null, locale: string, timeZone: string): string | null {
  if (value === null)
    return null
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(value))
}

function MatchRow({ card, locale, timeZone, origin }: { card: MatchCard, locale: string, timeZone: string, origin: string }) {
  const t = useTranslations('Matches')
  const kickoff = formatFixtureTime(card.kickoffAt, locale, timeZone)
  const liveTiming = card.liveMinute === null
    ? null
    : card.stoppageMinute === null
      ? t('liveMinute', { minute: card.liveMinute })
      : t('liveMinuteWithStoppage', { minute: card.liveMinute, stoppage: card.stoppageMinute })
  const timing = liveTiming ?? kickoff ?? t('states.kickoff_tbd')
  const observedAt = card.freshness.observedAt === null
    ? null
    : formatFixtureTime(card.freshness.observedAt, locale, timeZone)
  const freshness = card.freshness.state === 'unavailable'
    ? t('freshness.unavailable')
    : card.freshness.state === 'stale'
      ? t('freshness.stale', { time: observedAt ?? '—' })
      : card.freshness.state === 'provider_failed'
        ? t('freshness.provider_failed', { time: observedAt ?? '—' })
        : t('freshness.fresh', { time: observedAt ?? '—' })

  return (
    <article
      aria-label={`${card.homeTeam.name} — ${card.awayTeam.name}`}
      className="group relative overflow-hidden rounded-2xl border border-divider/70 bg-content1 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-primary" aria-hidden="true" />
      <div className="grid min-w-0 gap-4 p-4 pl-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:p-5 sm:pl-6">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
            <span className="min-w-0 break-words">{card.competition.competitionName}</span>
            <span aria-hidden="true">·</span>
            <span className="text-foreground">{timing}</span>
          </div>
          <p data-match-status className="mt-2 text-sm font-semibold text-primary">
            {t(`states.${card.state}`)}
          </p>

          <div className="mt-4 grid min-w-0 grid-cols-[minmax(0,1fr)_2.5rem] gap-x-4 gap-y-3 text-base sm:text-lg">
            <span className="min-w-0 break-words font-semibold text-foreground">{card.homeTeam.name}</span>
            <span className="text-right font-mono text-xl font-black tabular-nums text-foreground">
              {card.score?.home ?? '—'}
            </span>
            <span className="min-w-0 break-words font-semibold text-foreground">{card.awayTeam.name}</span>
            <span className="text-right font-mono text-xl font-black tabular-nums text-foreground">
              {card.score?.away ?? '—'}
            </span>
          </div>
          {card.score === null && <span className="sr-only">{t('scoreUnavailable')}</span>}

          <div className="mt-4 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted">
            <span>{freshness}</span>
            {card.notableChange !== null && (
              <span className="rounded-full bg-warning/15 px-2.5 py-1 font-semibold text-warning-foreground">
                {t(`notable.${card.notableChange.kind}`)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-end sm:justify-end">
          <a
            href={footballCoreHref(locale, `/matches/${encodeURIComponent(card.matchId)}`, { back: origin })}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-divider bg-background px-4 py-2 text-sm font-bold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:w-auto"
          >
            {t('actions.openMatch')}
          </a>
        </div>
      </div>
    </article>
  )
}

function LoadingMatches() {
  const t = useTranslations('Matches')
  return (
    <div aria-label={t('loading')} className="space-y-3" aria-busy="true">
      {[0, 1, 2].map(index => (
        <div key={index} className="h-44 animate-pulse rounded-2xl border border-divider/60 bg-content1" />
      ))}
    </div>
  )
}

function RetryState({ title, body, onRetry }: { title: string, body: string, onRetry: () => void }) {
  const t = useTranslations('Matches')
  return (
    <section className="rounded-2xl border border-danger/30 bg-danger/5 p-6" aria-labelledby="matches-retry-title">
      <h2 id="matches-retry-title" className="text-xl font-bold text-foreground">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-muted">{body}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-foreground px-4 text-sm font-bold text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {t('actions.retry')}
      </button>
    </section>
  )
}
interface CompetitionScopeOption {
  name: string
  season: number
}

function MatchFilters({
  base,
  competitions,
  locale,
  search,
  teams,
}: {
  base: string
  competitions: ReadonlyMap<string, CompetitionScopeOption>
  locale: string
  search: MatchesSearch
  teams: ReadonlyMap<string, string>
}) {
  const t = useTranslations('Matches')
  const initialScope = search.competition !== undefined && search.season !== undefined
    ? `${search.competition}:${search.season}`
    : ''
  const [selectedScope, setSelectedScope] = useState(initialScope)
  const [selectedTeam, setSelectedTeam] = useState(search.team ?? '')
  const [selectedDate, setSelectedDate] = useState(search.date ?? '')
  const [selectedCompetition = '', selectedSeason = ''] = selectedScope.split(':')
  const hasSelectedScope = selectedCompetition.length > 0 && selectedSeason.length > 0

  return (
    <details className="mt-5 rounded-2xl border border-divider/70 bg-content1 p-4">
      <summary className="min-h-11 cursor-pointer py-2 text-sm font-bold text-foreground">{t('filters.title')}</summary>
      <form action={base} method="get" className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <input type="hidden" name="view" value={search.view} />
        <label className="grid gap-1.5 text-sm font-semibold text-foreground">
          {t('filters.competition')}
          <select
            value={selectedScope}
            onChange={event => setSelectedScope(event.currentTarget.value)}
            className="min-h-11 rounded-xl border border-divider bg-background px-3"
          >
            <option value="">{t('filters.allCompetitions')}</option>
            {initialScope !== '' && !competitions.has(initialScope) && (
              <option value={initialScope}>{`${search.competition} · ${search.season}`}</option>
            )}
            {[...competitions].map(([scopeKey, competition]) => (
              <option key={scopeKey} value={scopeKey}>{`${competition.name} · ${competition.season}`}</option>
            ))}
          </select>
        </label>
        <input
          type="hidden"
          name={hasSelectedScope ? 'competition' : undefined}
          value={selectedCompetition}
        />
        <input
          type="hidden"
          name={hasSelectedScope ? 'season' : undefined}
          value={selectedSeason}
        />
        <label className="grid gap-1.5 text-sm font-semibold text-foreground">
          {t('filters.team')}
          <select
            name={selectedTeam.length > 0 ? 'team' : undefined}
            value={selectedTeam}
            onChange={event => setSelectedTeam(event.currentTarget.value)}
            className="min-h-11 rounded-xl border border-divider bg-background px-3"
          >
            <option value="">{t('filters.allTeams')}</option>
            {search.team !== undefined && !teams.has(search.team) && <option value={search.team}>{search.team}</option>}
            {[...teams].map(([teamId, name]) => <option key={teamId} value={teamId}>{name}</option>)}
          </select>
        </label>
        <label className="grid gap-1.5 text-sm font-semibold text-foreground">
          {t('filters.date')}
          <input
            name={selectedDate.length > 0 ? 'date' : undefined}
            type="date"
            value={selectedDate}
            onChange={event => setSelectedDate(event.currentTarget.value)}
            className="min-h-11 rounded-xl border border-divider bg-background px-3"
          />
        </label>
        <input type="hidden" name="timeZone" value={search.timeZone} />
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-4">
          <button type="submit" className="inline-flex min-h-11 items-center rounded-xl bg-primary px-4 text-sm font-bold text-white">{t('filters.apply')}</button>
          <a href={matchesHref(locale, { view: search.view, timeZone: search.timeZone, page: 1 }, {})} className="inline-flex min-h-11 items-center rounded-xl px-4 text-sm font-bold text-foreground">{t('filters.clear')}</a>
          <span className="text-xs text-muted">{t('filters.timeZone', { timeZone: search.timeZone })}</span>
        </div>
      </form>
    </details>
  )
}

export function MatchesView({ data, search, isPending, isFetching, error, origin, onRetry }: MatchesViewProps) {
  const t = useTranslations('Matches')
  const locale = useLocale()
  const base = footballCoreHref(locale, '/matches')
  const matchData = data?.data
  const filtered = search.competition !== undefined || search.team !== undefined || search.date !== undefined
  const readModelUnavailable = error instanceof PublicFootballRequestError
    && error.status === 503
    && error.code === 'read_model_unavailable'
  const staleFacts = matchData !== undefined
    && (error !== null
      || matchData.scopeFreshness.state === 'stale'
      || (matchData.scopeFreshness.state === 'provider_failed' && matchData.scopeFreshness.lastGoodAvailable))
  const availableCards = [
    ...(matchData?.items ?? []),
    ...(matchData?.noLiveRecovery?.upcoming ?? []),
  ]
  const competitions = new Map<string, CompetitionScopeOption>()
  const teams = new Map<string, string>()
  for (const card of availableCards) {
    const scopeKey = `${card.competition.competitionCode}:${card.competition.season}`
    competitions.set(scopeKey, {
      name: card.competition.competitionName,
      season: card.competition.season,
    })
    teams.set(card.homeTeam.teamId, card.homeTeam.name)
    teams.set(card.awayTeam.teamId, card.awayTeam.name)
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:py-10 lg:px-6">
      <header className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">BallPeek</p>
        <h1 className="mt-2 text-4xl font-black tracking-tight text-foreground sm:text-5xl">{t('title')}</h1>
        <p className="mt-3 text-base leading-7 text-muted sm:text-lg">{t('subtitle')}</p>
        <p className="mt-2 text-sm text-muted">{t('freshnessSummary')}</p>
      </header>

      <nav aria-label={t('viewsLabel')} className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {MATCH_VIEWS.map((view) => {
          const className = `inline-flex min-h-11 items-center justify-center rounded-xl px-3 text-center text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            search.view === view ? 'bg-foreground text-background' : 'border border-divider bg-content1 text-foreground hover:border-primary'
          }`
          const targetSearch = { ...search, view, page: 1 }
          return locale === 'zh-CN'
            ? (
                <Link key={view} to="/$locale/matches" params={{ locale: 'zh-CN' }} search={targetSearch} aria-current={search.view === view ? 'page' : undefined} className={className}>
                  {t(`views.${view}`)}
                </Link>
              )
            : (
                <Link key={view} to="/matches" search={targetSearch} aria-current={search.view === view ? 'page' : undefined} className={className}>
                  {t(`views.${view}`)}
                </Link>
              )
        })}
      </nav>

      <MatchFilters
        key={`${search.competition ?? ''}:${search.season ?? ''}`}
        base={base}
        competitions={competitions}
        locale={locale}
        search={search}
        teams={teams}
      />

      <div className="mt-6" aria-live="polite">
        {isFetching && !isPending && <p role="status" className="mb-3 text-sm font-semibold text-primary">{t('updating')}</p>}
        {staleFacts && (
          <section role="alert" className="mb-4 rounded-2xl border border-warning/40 bg-warning/10 p-4">
            <h2 className="font-bold text-foreground">{t('freshness.staleTitle')}</h2>
            <p className="mt-1 text-sm text-foreground/80">{t('freshness.staleBody')}</p>
            <button type="button" onClick={onRetry} className="mt-3 min-h-11 rounded-xl px-3 text-sm font-bold text-foreground">{t('actions.retry')}</button>
          </section>
        )}
      </div>

      {isPending && data === undefined
        ? <LoadingMatches />
        : readModelUnavailable
          ? <RetryState title={t('sectionFailure.title')} body={t('sectionFailure.body')} onRetry={onRetry} />
          : error !== null && data === undefined
            ? <RetryState title={t('networkFailure.title')} body={t('networkFailure.body')} onRetry={onRetry} />
            : matchData !== undefined && matchData.items.length > 0
              ? (
                  <section className="space-y-3" aria-label={t(`views.${matchData.view}`)}>
                    {matchData.items.map(card => <MatchRow key={card.matchId} card={card} locale={locale} timeZone={matchData.filters.timeZone} origin={origin} />)}
                  </section>
                )
              : matchData !== undefined && search.view === 'live' && (matchData.noLiveRecovery?.upcoming.length ?? 0) > 0
                ? (
                    <section className="rounded-2xl border border-divider/70 bg-content1 p-5">
                      <h2 className="text-xl font-bold text-foreground">{t('noLive.title')}</h2>
                      <p className="mt-2 text-sm text-muted">{t('noLive.body')}</p>
                      {matchData.noLiveRecovery?.upcoming.map(card => (
                        <div key={card.matchId} className="mt-4">
                          <MatchRow card={card} locale={locale} timeZone={matchData.filters.timeZone} origin={origin} />
                        </div>
                      ))}
                      <a href={matchesHref(locale, search, { view: 'upcoming', page: 1 })} className="mt-4 inline-flex min-h-11 items-center rounded-xl font-bold text-primary">
                        {t('noLive.action')}
                      </a>
                    </section>
                  )
                : matchData !== undefined && filtered
                  ? (
                      <section className="rounded-2xl border border-divider/70 bg-content1 p-5">
                        <h2 className="text-xl font-bold text-foreground">{t('filterEmpty.title')}</h2>
                        <p className="mt-2 text-sm text-muted">{t('filterEmpty.body')}</p>
                        <a href={matchesHref(locale, { view: search.view, timeZone: search.timeZone, page: 1 }, {})} className="mt-4 inline-flex min-h-11 items-center font-bold text-primary">{t('filters.clear')}</a>
                      </section>
                    )
                  : (
                      <section className="rounded-2xl border border-divider/70 bg-content1 p-5">
                        <h2 className="text-xl font-bold text-foreground">{t('empty.title')}</h2>
                        <p className="mt-2 text-sm text-muted">{t('empty.body')}</p>
                      </section>
                    )}

      {matchData !== undefined && matchData.pagination.totalPages > 1 && (
        <nav aria-label={t('pagination.page', { page: matchData.pagination.page, totalPages: matchData.pagination.totalPages })} className="mt-6 flex items-center justify-between gap-3">
          <a href={matchesHref(locale, search, { page: Math.max(1, matchData.pagination.page - 1) })} aria-disabled={matchData.pagination.page === 1} className="inline-flex min-h-11 items-center rounded-xl px-3 font-bold text-primary">{t('pagination.previous')}</a>
          <span className="text-sm text-muted">{t('pagination.page', { page: matchData.pagination.page, totalPages: matchData.pagination.totalPages })}</span>
          <a href={matchesHref(locale, search, { page: matchData.pagination.page + 1 })} aria-disabled={!matchData.pagination.hasNextPage} className="inline-flex min-h-11 items-center rounded-xl px-3 font-bold text-primary">{t('pagination.next')}</a>
        </nav>
      )}

      <aside className="mt-8 grid gap-2 rounded-2xl border border-divider/70 bg-content1 p-5 text-sm text-muted">
        <p className="font-semibold text-foreground">{t('marketUnavailable')}</p>
        <p>{t('sourceDisclosure')}</p>
      </aside>
    </div>
  )
}

export function MatchesPage({ search }: { search: MatchesSearchInput }) {
  const origin = useCurrentRelativeLocation()
  const resolvedSearch = resolveMatchesSearch(search)
  const query = useQuery(matchesQueryOptions(resolvedSearch))
  return (
    <MatchesView
      data={query.data}
      search={resolvedSearch}
      isPending={query.isPending}
      isFetching={query.isFetching}
      error={query.error}
      origin={origin}
      onRetry={() => { void query.refetch() }}
    />
  )
}
